import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

// shimmer animation
const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

// base skeleton block
const SkeletonBlock = styled.div`
  background: ${({ theme }) => theme.palette.greyGrey4};
  background-image: linear-gradient(
    90deg,
    ${({ theme }) => theme.palette.greyGrey4},
    #f0f0f0e1,
    ${({ theme }) => theme.palette.greyGrey4}
  );
  background-size: 200px 100%;
  background-repeat: no-repeat;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 4px;
`;

// container matching LocumCard
const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  background-color: ${({ theme }) => theme.palette.backgroundColor};
  border-radius: 0.25rem;
  min-height: 12rem;
  max-height: 12rem;
  overflow: hidden;
  width: 100%;
  min-width: 390px;
  max-width: 390px;

  &.pc {
    min-height: 10rem;
    max-height: 12rem;
    min-width: 300px;
    max-width: 300px;
    background-color: ${({ theme }) => theme.palette.white};
    border: 1.4px solid ${({ theme }) => theme.palette.greyGrey3};
  }

  @media (max-width: 860px) {
    background-color: ${({ theme }) => theme.palette.white};
    box-shadow: -1px 5px 5px 0px rgba(0, 0, 0, 0.03);
    width: 100%;
    min-width: 0;
    max-width: 600px;
  }
`;

// upper section placeholder
const UpperSectionSkeleton = styled.div`
  display: grid;
  grid-template-columns: 50px auto;
  gap: 0.5rem;
  padding: 0.5rem;

  @media (max-width: 480px) {
    grid-template-columns: 30px auto;
  }
`;

// avatar circle
const AvatarSkeleton = styled(SkeletonBlock)`
  width: 50px;
  height: 50px;
  border-radius: 50%;

  @media (max-width: 480px) {
    width: 30px;
    height: 30px;
  }
`;

// text placeholders
const TitleSkeleton = styled(SkeletonBlock)`
  width: 60%;
  height: 1.25rem;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    height: 1rem;
  }
`;
const DescSkeleton = styled(SkeletonBlock)`
  width: 80%;
  height: 0.872rem;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    height: 0.75rem;
  }
`;
const XpSkeleton = styled(SkeletonBlock)`
  width: 30%;
  height: 0.75rem;

  @media (max-width: 768px) {
    height: 0.6rem;
  }
`;

// bottom section placeholder
const BottomSectionSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.5rem 0.875rem;
  border-top: 1px solid ${({ theme }) => theme.palette.borderColor};
`;

// row of two placeholders
const RowSkeleton = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;
const CellSkeleton = styled(SkeletonBlock)<{ width: string }>`
  width: ${({ width }) => width};
  height: 1.1rem;

  @media (max-width: 768px) {
    height: 1rem;
  }
`;

interface Props {
  className?: string;
}

const LocumCardSkeleton: React.FC<Props> = ({ className }) => (
  <Container className={className}>
    <UpperSectionSkeleton>
      <AvatarSkeleton />
      <div>
        <TitleSkeleton />
        <DescSkeleton />
        <XpSkeleton />
      </div>
    </UpperSectionSkeleton>
    <BottomSectionSkeleton>
      <RowSkeleton>
        <CellSkeleton width="40%" />
        <CellSkeleton width="40%" />
      </RowSkeleton>
      <RowSkeleton>
        <CellSkeleton width="40%" />
        <CellSkeleton width="40%" />
      </RowSkeleton>
    </BottomSectionSkeleton>
  </Container>
);

export default LocumCardSkeleton;
